/**
 * dealTimeline.ts — the deal page's ONE timeline (deal detail slice 2, §39.9
 * order 2; spec §3.0 and §3.4 C), composed client-side from the three reads
 * the page already makes: the deal's activities, its field history and its
 * stage events. Pure, so vitest pins every rule without a DOM.
 *
 * The inputs are plain arrays on purpose. §39.9's slice 5 adds no timeline
 * feed, so this merge is the design; if a server feed ever replaces it, only
 * the inputs change, never the entries' shape or the card.
 *
 * B-1 still holds: nothing here writes a stage row into field history or the
 * reverse. The merge is for display only.
 *
 * §3.0 / §4 — NO DEVELOPER STRINGS. Every value below is mapped to a sentence.
 * A value the map does not know renders as a plain generic sentence and its
 * raw text is LOGGED (logRawCopy), never shown. Every title is built from
 * values the read path actually carries; nothing is guessed.
 */
import type { SalesActivity, SalesDealStageEvent, SalesFieldHistoryEntry } from '../../../../api/types'
import type { DealDetail } from '../../types'
import { SLUG_TO_STAGE } from '../../api'
import { formatDay } from './dealRail'
import { compactMoney } from '../../../../lib/money'
import { daysBeforeMove, daysLabel } from './stageBar'
import { suggestionLabel, logRawCopy } from './dealCopy'
import { FORECAST_CATEGORY_OPTIONS, PRIORITY_OPTIONS, SOURCE_OPTIONS } from './vocab'
import { dueDayOf } from './workingDays'
import { isTaskDone } from './nextSteps'
import { roleLabel } from './dealPeople'

export type TimelineIcon = 'call' | 'email' | 'meeting' | 'note' | 'task' | 'stage' | 'field' | 'created'
export type TimelineKind = 'activity' | 'change'
export type TimelineFilter = 'all' | 'activity' | 'change'

export interface TimelineItem {
  key: string
  kind: TimelineKind
  icon: TimelineIcon
  title: string
  /** Absent when the row has nothing honest to add. */
  detail?: string
  /** ISO instant, for ordering and the meta line. */
  at: string
  /** Who did it, per the actor ladder. For an activity (slice 8): who LOGGED
   *  it (createdByUserId, 0133) — absent when no creator was recorded (every
   *  activity created before 0133), never the owner. */
  who?: string
}

export interface TimelineJoins {
  employeeName?: (id: string) => string | undefined
  accountName?: (id: string) => string | undefined
  contactName?: (id: string) => string | undefined
}

/** The actor ladder (slice 2, by instruction): the display name the read path
 *  carried, else "Unknown user". The wire carries no actor email (AN-5), so
 *  the email rung has no source; an id is never shown. */
export function actorName(userId?: string, displayName?: string): string {
  const name = (displayName ?? '').trim()
  if (userId && name && !/\S+@\S+\.\S+/.test(name)) return name
  return 'Unknown user'
}

/** Two moves within this window that reverse each other are an Undo pair.
 *  The toast offers Undo for 5 s; the margin covers the round trip. */
export const UNDO_PAIR_MS = 60_000

const ACTIVITY_ICON: Record<string, TimelineIcon> = { call: 'call', email: 'email', meeting: 'meeting', note: 'note', task: 'task' }

function stageName(slug: string | undefined): string | null {
  if (!slug) return null
  const label = SLUG_TO_STAGE[slug]
  if (label) return label
  logRawCopy('stage', slug)
  return null
}

function moveTitle(slug: string): string {
  if (slug === 'closed_won') return 'Marked won'
  if (slug === 'closed_lost') return 'Marked lost'
  const name = stageName(slug)
  return name ? `Moved to ${name}` : 'Moved to another stage'
}

const time = (iso: string) => Date.parse(iso)
const sameUtcDay = (a: string, b: string) => a.slice(0, 10) === b.slice(0, 10)
const optionLabel = (opts: readonly { value: string; label: string }[], v: string) => opts.find(o => o.value === v)?.label

/** One stored value as words, or null when it has no honest sentence. */
function valueWords(field: string, v: string, deal: DealDetail, joins: TimelineJoins, currencyChanged: boolean): string | null {
  switch (field) {
    case 'title': case 'company': return v
    case 'value_pence': case 'forecast_override_amount_pence': {
      // The row carries pence and NO currency. The deal's currency names the
      // amount only while the currency has never changed on record.
      const n = Number(v)
      return Number.isFinite(n) && !currencyChanged ? compactMoney(n, deal.currency) : null
    }
    case 'probability': return /^\d{1,3}$/.test(v) ? `${v}%` : null
    case 'expected_close': return Number.isFinite(Date.parse(v)) ? formatDay(v, 'utc') : null
    case 'currency': return /^[A-Z]{3}$/.test(v) ? v : null
    case 'assigned_employee_id': return joins.employeeName?.(v) ?? null
    case 'account_id': return joins.accountName?.(v) ?? null
    case 'contact_id': return joins.contactName?.(v) ?? null
    case 'source': return optionLabel(SOURCE_OPTIONS, v) ?? null
    case 'priority': return optionLabel(PRIORITY_OPTIONS, v) ?? null
    case 'forecast_category': case 'forecast_override_category': return optionLabel(FORECAST_CATEGORY_OPTIONS, v) ?? null
    default: return null
  }
}

const FIELD_NAME: Record<string, string> = {
  title: 'Title', company: 'Company', value_pence: 'Value', currency: 'Currency', probability: 'Probability',
  expected_close: 'Expected close', assigned_employee_id: 'Owner', account_id: 'Account', contact_id: 'Primary contact',
  source: 'Source', priority: 'Priority', forecast_category: 'Forecast category',
  forecast_override_category: 'Forecast override', forecast_override_amount_pence: 'Forecast override amount',
}

/** A field edit as a title and an optional detail (spec §3.4 C: "{Field}
 *  changed to {value}" / "Was {old value}", the detail omitted when the old
 *  value was empty). */
function fieldEntry(h: SalesFieldHistoryEntry, deal: DealDetail, joins: TimelineJoins, currencyChanged: boolean): { title: string; detail?: string } {
  const f = h.field
  if (f === 'notes') {
    const text = (h.newValue ?? '').trim()
    if (!text) return { title: 'Notes cleared' }
    return { title: 'Notes updated', detail: text.length > 160 ? `${text.slice(0, 157)}…` : text }
  }
  if (f === 'owner_link_kept') {
    return h.newValue ? { title: 'Owner kept unlinked' } : { title: 'Owner no longer kept unlinked' }
  }
  const name = FIELD_NAME[f]
  if (!name) {
    logRawCopy('history field', f)
    return { title: 'A detail of this deal changed' }
  }
  const empty = (v: string | null) => v === null || v === ''
  if (empty(h.newValue)) {
    if (f === 'forecast_override_category') return { title: 'Forecast override removed' }
    return { title: `${name} cleared` }
  }
  const now = valueWords(f, h.newValue as string, deal, joins, currencyChanged)
  if (now === null) logRawCopy(`history ${f}`, h.newValue as string)
  const was = empty(h.oldValue) ? null : valueWords(f, h.oldValue as string, deal, joins, currencyChanged)
  return {
    title: now === null ? `${name} changed` : `${name} changed to ${now}`,
    ...(was !== null && now !== null ? { detail: `Was ${was}` } : {}),
  }
}

const ICON_RANK: Partial<Record<TimelineIcon, number>> = { stage: 0, field: 1, created: 2 }

/** Slice 8: who logged an activity — the actor ladder over createdBy*, or
 *  nothing when no creator was recorded (pre-0133 rows: never "Unknown"). */
function creator(a: SalesActivity): { who?: string } {
  return a.createdByUserId ? { who: actorName(a.createdByUserId, a.createdByDisplayName) } : {}
}

export interface TimelineInput {
  deal: DealDetail
  activities: readonly SalesActivity[]
  history: readonly SalesFieldHistoryEntry[]
  events: readonly SalesDealStageEvent[]
  joins?: TimelineJoins
}

export function buildTimeline({ deal, activities, history, events, joins = {} }: TimelineInput): TimelineItem[] {
  const items: TimelineItem[] = []

  // ── Activities (call, email, meeting, note; tasks as "Task added", and a
  // second "Task completed" entry once one is done — slice 4) ──────────────
  for (const a of activities) {
    const icon = ACTIVITY_ICON[a.activityType]
    if (!icon) logRawCopy('activity type', a.activityType)
    if (a.activityType === 'task') {
      // The due day is the LONDON day the instant names (R1), as the Next
      // steps card shows it.
      const due = dueDayOf(a.dueAt)
      const parts = [
        due ? `Due ${formatDay(due, 'utc')}` : '',
        a.assignedTo ? `assigned to ${a.assignedTo}` : '',
      ].filter(Boolean)
      items.push({ key: `act:${a.id}`, kind: 'activity', icon: 'task', title: `Task added: ${a.subject}`, ...(parts.length ? { detail: parts.join(' · ') } : {}), at: a.createdAt, ...creator(a) })
      // Spec §3.4 B: "ticking a task completes it and writes a timeline
      // entry". §3.4 C's table has no row for it; the sentence is ours. Done
      // is the STATUS: a reopened task keeps a stale completed_at.
      if (isTaskDone(a) && a.completedAt && Number.isFinite(Date.parse(a.completedAt))) {
        items.push({ key: `done:${a.id}`, kind: 'activity', icon: 'task', title: `Task completed: ${a.subject}`, at: a.completedAt })
      }
      continue
    }
    items.push({
      key: `act:${a.id}`, kind: 'activity', icon: icon ?? 'note', title: a.subject,
      ...(a.body ? { detail: a.body } : {}), at: a.createdAt, ...creator(a),
    })
  }

  // ── §3.0 dedupe: the created row + the birth stage event + the deal's own
  // birth = ONE "Deal created in {stage}" ───────────────────────────────────
  const created = history.find(h => h.field === 'created')
  const birth = events.find(e => !e.fromStage)
  const bornIn = stageName(birth?.toStage)
  const facts = [
    compactMoney(deal.amountPence, deal.currency),
    deal.expectedClose ? `close ${formatDay(deal.expectedClose, 'utc')}` : '',
    deal.owner ? `owner ${deal.owner}` : '',
  ].filter(Boolean)
  const actorRow = created ?? birth
  items.push({
    key: 'birth', kind: 'change', icon: 'created',
    title: bornIn ? `Deal created in ${bornIn}` : 'Deal created',
    ...(facts.length ? { detail: facts.join(' · ') } : {}),
    at: created?.changedAt ?? birth?.changedAt ?? deal.createdAt,
    ...(actorRow ? { who: actorName(actorRow.actorUserId, actorRow.actorDisplayName) } : {}),
  })

  // ── Stage moves: each its own entry; an Undo pair reads as one ───────────
  const moves = [...events].filter(e => e.fromStage).sort((a, b) => time(a.changedAt) - time(b.changedAt))
  moves.forEach((e, i) => {
    const prev = i > 0 ? moves[i - 1] : undefined
    const isUndo = Boolean(prev && prev.fromStage === e.toStage && prev.toStage === e.fromStage
      && time(e.changedAt) - time(prev.changedAt) <= UNDO_PAIR_MS)
    let title = moveTitle(e.toStage)
    let detail: string | undefined
    if (isUndo) {
      const back = stageName(e.toStage)
      title = back ? `Moved back to ${back}` : 'Moved back to the previous stage'
      const undone = stageName(e.fromStage)
      detail = undone ? `Undid the move to ${undone}` : undefined
    } else {
      const days = daysBeforeMove(events as SalesDealStageEvent[], e.id)
      const from = stageName(e.fromStage)
      if (days !== null && from) detail = `After ${daysLabel(days)} in ${from}`
    }
    items.push({
      key: `stage:${e.id}`, kind: 'change', icon: 'stage', title, ...(detail ? { detail } : {}),
      at: e.changedAt, who: actorName(e.actorUserId, e.actorDisplayName),
    })
  })

  // ── Field edits, with the §3.0 suggestion merge ──────────────────────────
  const currencyChanged = history.some(h => h.field === 'currency')
  const edits = history.filter(h => h.field !== 'created')
  const consumed = new Set<string>()
  for (const s of edits.filter(h => h.field === 'forecast_category_suggestion')) {
    const accepted = edits.find(c => c.field === 'forecast_category' && !consumed.has(c.id) && sameUtcDay(c.changedAt, s.changedAt) && c.newValue)
    consumed.add(s.id)
    const why = suggestionLabel(s.newValue)
    if (accepted) {
      consumed.add(accepted.id)
      const cat = optionLabel(FORECAST_CATEGORY_OPTIONS, accepted.newValue as string)
      items.push({
        key: `field:${accepted.id}`, kind: 'change', icon: 'field',
        title: cat ? `Forecast category set to ${cat}` : 'Forecast category set',
        detail: why.startsWith('Suggested by') ? `${why}, then accepted` : 'Suggested by Klaxiora, then accepted',
        at: accepted.changedAt, who: actorName(accepted.actorUserId, accepted.actorDisplayName),
      })
    } else {
      items.push({
        key: `field:${s.id}`, kind: 'change', icon: 'field', title: 'Klaxiora suggested a forecast category',
        ...(why.startsWith('Suggested by') ? { detail: why } : {}),
        at: s.changedAt, who: actorName(s.actorUserId, s.actorDisplayName),
      })
    }
  }
  // ── Slice 8: People and the close-date Keep (0133's history fields) ──────
  // One write of the link routes can leave two rows at ONE instant (NOW() is
  // the transaction's): a link that made the contact primary (contact_linked
  // + contact_id → it) reads as ONE "linked as primary contact" (spec §3.4 C);
  // unlinking the primary (contact_unlinked + contact_id → ∅) as ONE
  // "unlinked". A set-primary (contact_id alone, as the deal PUT writes too)
  // reads "set as primary contact". A contact that no longer exists is named
  // "A contact" — never an id.
  const who = (h: SalesFieldHistoryEntry) => actorName(h.actorUserId, h.actorDisplayName)
  const nameOf = (id: string | null) => (id ? joins.contactName?.(id) : undefined) ?? 'A contact'
  const twin = (h: SalesFieldHistoryEntry, match: (c: SalesFieldHistoryEntry) => boolean) =>
    edits.find(c => c.field === 'contact_id' && !consumed.has(c.id) && c.changedAt === h.changedAt && match(c))
  for (const h of edits.filter(e => e.field === 'contact_linked' || e.field === 'contact_unlinked')) {
    consumed.add(h.id)
    if (h.field === 'contact_linked') {
      const primary = twin(h, c => c.newValue === h.newValue)
      if (primary) consumed.add(primary.id)
      items.push({ key: `field:${h.id}`, kind: 'change', icon: 'field',
        title: `${nameOf(h.newValue)} linked${primary ? ' as primary contact' : ''}`, at: h.changedAt, who: who(h) })
    } else {
      const cleared = twin(h, c => c.oldValue === h.oldValue && (c.newValue === null || c.newValue === ''))
      if (cleared) consumed.add(cleared.id)
      items.push({ key: `field:${h.id}`, kind: 'change', icon: 'field',
        title: `${nameOf(h.oldValue)} unlinked`, ...(cleared ? { detail: 'No primary contact now' } : {}), at: h.changedAt, who: who(h) })
    }
  }
  for (const h of edits.filter(e => (e.field === 'contact_id' && e.newValue) || e.field === 'close_date_confirmed')) {
    if (consumed.has(h.id)) continue
    consumed.add(h.id)
    if (h.field === 'close_date_confirmed') {
      const day = h.newValue && Number.isFinite(Date.parse(h.newValue)) ? formatDay(h.newValue, 'utc') : null
      items.push({ key: `field:${h.id}`, kind: 'change', icon: 'field', title: 'Close date confirmed', ...(day ? { detail: day } : {}), at: h.changedAt, who: who(h) })
    } else {
      const was = h.oldValue ? joins.contactName?.(h.oldValue) : undefined
      items.push({ key: `field:${h.id}`, kind: 'change', icon: 'field', title: `${nameOf(h.newValue)} set as primary contact`,
        ...(was ? { detail: `Was ${was}` } : {}), at: h.changedAt, who: who(h) })
    }
  }
  // R6 (0139): a committee role set or cleared — old/new are
  // "<contactId>:<role>", NULL for no role. "James Nova set as Decision maker"
  // (was Champion), or "James Nova's role cleared" (was Decision maker).
  const roleSide = (v: string | null) => {
    if (!v) return null
    const i = v.indexOf(':')
    return i < 0 ? null : { contactId: v.slice(0, i), label: roleLabel(v.slice(i + 1)) }
  }
  for (const h of edits.filter(e => e.field === 'contact_role')) {
    consumed.add(h.id)
    const was = roleSide(h.oldValue), now = roleSide(h.newValue)
    const person = nameOf(now?.contactId ?? was?.contactId ?? null)
    items.push({ key: `field:${h.id}`, kind: 'change', icon: 'field',
      title: now?.label ? `${person} set as ${now.label}` : `${person}'s role cleared`,
      ...(was?.label ? { detail: `Was ${was.label}` } : {}), at: h.changedAt, who: who(h) })
  }
  for (const h of edits) {
    if (consumed.has(h.id)) continue
    const { title, detail } = fieldEntry(h, deal, joins, currencyChanged)
    items.push({
      key: `field:${h.id}`, kind: 'change', icon: 'field', title, ...(detail ? { detail } : {}),
      at: h.changedAt, who: actorName(h.actorUserId, h.actorDisplayName),
    })
  }

  // Newest first; ties: stage, then field, then creation (the birth sits at the
  // bottom of its instant), then activities, then key — a total, stable order.
  return items.sort((a, b) => {
    const d = time(b.at) - time(a.at)
    if (d !== 0 && Number.isFinite(d)) return d
    const ra = ICON_RANK[a.icon] ?? 3, rb = ICON_RANK[b.icon] ?? 3
    if (ra !== rb) return ra - rb
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
  })
}

export function filterTimeline(items: readonly TimelineItem[], filter: TimelineFilter): TimelineItem[] {
  return filter === 'all' ? [...items] : items.filter(i => i.kind === filter)
}
